import React, { useCallback, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import { i18n } from '@lingui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Tooltip from '@material-ui/core/Tooltip';
import CustomSpeedDialog from './CustomSpeedDialog';
import {
    INTERFACE_ACTION_URLS,
    INTERFACE_POLLING_AUTO,
    INTERFACE_POLLING_DISABLE,
    INTERFACE_POLLING_ENABLE,
    INTERFACE_PRIORITY_AUTO,
    INTERFACE_PRIORITY_HIGH,
    INTERFACE_PRIORITY_LOW,
    INTERFACE_SPEED,
    SPEED_100M,
    SPEED_10G,
    SPEED_1G,
    SPEED_AUTO,
} from '../device/DeviceConstants';
import { MenusItemsArrayButton } from '../MenusItemsArrayButton';

const autoPollingTooltipHelpTip = i18n._(t`Auto Enabled/Disabled polling by interface selection setting`);
const autoPriorityTooltipHelpTip = i18n._(t`Automatically set to High/Low priority by Interface Priority Setting`);

/**
 * Three dropdown menus for interface table actions: Polling, Priority, Speed.
 * All menus are disabled when no valid selection.
 * @param {Object} props
 * @param {boolean} props.disabled - Disables all menu buttons when true
 * @param {Function} props.onAction - Callback: (url, extraPayload?) => void. Parent handles POST + reload.
 */
export const InterfaceActionMenusArray = ({ actionUrls, disabled, onAction }) => {
    const [speedDialogOpen, setSpeedDialogOpen] = useState(false);

    const handleSpeedAction = useCallback(
        (url, speed) => {
            onAction(url, { speed });
        },
        [onAction]
    );

    const handleCustomSpeedSave = useCallback(
        (speedBps) => {
            setSpeedDialogOpen(false);
            handleSpeedAction(actionUrls[INTERFACE_SPEED], speedBps);
        },
        [actionUrls, handleSpeedAction]
    );

    return (
        <>
            {/* Polling Menu */}
            <MenusItemsArrayButton key="Polling" label={<Trans>Polling</Trans>} disabled={disabled}>
                {(closeMenu) => [
                    <MenuItem
                        key={INTERFACE_POLLING_ENABLE}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_POLLING_ENABLE]);
                        }}
                    >
                        <Trans>{INTERFACE_POLLING_ENABLE}</Trans>
                    </MenuItem>,
                    <MenuItem
                        key={INTERFACE_POLLING_DISABLE}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_POLLING_DISABLE]);
                        }}
                    >
                        <Trans>{INTERFACE_POLLING_DISABLE}</Trans>
                    </MenuItem>,
                    <MenuItem
                        key={INTERFACE_POLLING_AUTO}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_POLLING_AUTO]);
                        }}
                    >
                        <Trans>Auto</Trans>
                        <Tooltip title={autoPollingTooltipHelpTip}>
                            <HelpOutlineIcon
                                fontSize="small"
                                color="action"
                                style={{ marginLeft: 4, marginBottom: 4 }}
                            />
                        </Tooltip>
                    </MenuItem>,
                ]}
            </MenusItemsArrayButton>

            {/* Priority Menu */}
            <MenusItemsArrayButton key="Priority" label={<Trans>Priority</Trans>} disabled={disabled}>
                {(closeMenu) => [
                    <MenuItem
                        key={INTERFACE_PRIORITY_HIGH}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_PRIORITY_HIGH]);
                        }}
                    >
                        <Trans>{INTERFACE_PRIORITY_HIGH}</Trans>
                    </MenuItem>,
                    <MenuItem
                        key={INTERFACE_PRIORITY_LOW}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_PRIORITY_LOW]);
                        }}
                    >
                        <Trans>{INTERFACE_PRIORITY_LOW}</Trans>
                    </MenuItem>,
                    <MenuItem
                        key={INTERFACE_PRIORITY_AUTO}
                        onClick={() => {
                            closeMenu();
                            onAction(actionUrls[INTERFACE_PRIORITY_AUTO]);
                        }}
                    >
                        <Trans>Auto</Trans>
                        <Tooltip title={autoPriorityTooltipHelpTip}>
                            <HelpOutlineIcon
                                fontSize="small"
                                color="action"
                                style={{ marginLeft: 4, marginBottom: 4 }}
                            />
                        </Tooltip>
                    </MenuItem>,
                ]}
            </MenusItemsArrayButton>

            {/* Speed Menu */}
            <MenusItemsArrayButton key="Speed" label={<Trans>Speed</Trans>} disabled={disabled}>
                {(closeMenu) => [
                    <MenuItem
                        key={SPEED_100M}
                        onClick={() => {
                            closeMenu();
                            handleSpeedAction(actionUrls[INTERFACE_SPEED], SPEED_100M);
                        }}
                    >
                        100M
                    </MenuItem>,
                    <MenuItem
                        key={SPEED_1G}
                        onClick={() => {
                            closeMenu();
                            handleSpeedAction(actionUrls[INTERFACE_SPEED], SPEED_1G);
                        }}
                    >
                        1G
                    </MenuItem>,
                    <MenuItem
                        key={SPEED_10G}
                        onClick={() => {
                            closeMenu();
                            handleSpeedAction(actionUrls[INTERFACE_SPEED], SPEED_10G);
                        }}
                    >
                        10G
                    </MenuItem>,
                    <MenuItem
                        key="custom"
                        onClick={() => {
                            closeMenu();
                            setSpeedDialogOpen(true);
                        }}
                    >
                        <Trans>Custom</Trans>
                    </MenuItem>,
                    <MenuItem
                        key="Auto"
                        onClick={() => {
                            closeMenu();
                            handleSpeedAction(actionUrls[INTERFACE_SPEED], SPEED_AUTO);
                        }}
                    >
                        <Trans>Auto</Trans>
                    </MenuItem>,
                ]}
            </MenusItemsArrayButton>

            <CustomSpeedDialog
                open={speedDialogOpen}
                onClose={() => setSpeedDialogOpen(false)}
                onSave={handleCustomSpeedSave}
            />
        </>
    );
};

const InterfaceActionMenusBox = ({ disabled, onAction }) => {
    return (
        <Box display="flex" style={{ gap: '8px' }} mb={1}>
            <InterfaceActionMenusArray actionUrls={INTERFACE_ACTION_URLS} disabled={disabled} onAction={onAction} />
        </Box>
    );
};

export default InterfaceActionMenusBox;
